import { Connection, PublicKey } from '@solana/web3.js';
import { logError, safeExecute } from './errorHandler';
import logger from './logger';

// TopBlast token mint address - update this with your new CA
const TOPBLAST_TOKEN_MINT = process.env.REACT_APP_TOPBLAST_TOKEN_ADDRESS || "F4XcNucxLXkNywr27994xhJLcZ8sKbWqoaS8xf47pump";

// Solana RPC connection
const connection = new Connection(
  process.env.REACT_APP_SOLANA_RPC_URL || 'https://api.mainnet-beta.solana.com',
  'confirmed'
);

// Check if valid Solana address - currently unused but kept for future use
// const isValidSolanaAddress = (address) => {
//   try {
//     new PublicKey(address);
//     return true;
//   } catch {
//     return false;
//   }
// };

export class WalletService {
  constructor() {
    this.wallet = null;
    this.publicKey = null;
    this.isConnected = false;
  }

  // Wallet configurations for all supported wallets
  static WALLET_CONFIGS = {
    phantom: {
      name: 'Phantom',
      icon: '👻',
      // Enhanced Phantom detection for Brave browser
      getProvider: () => {
        logger.log('🔍 Searching for Phantom provider...');

        // Method 1: Direct phantom object
        if (window.phantom?.solana) {
          logger.log('✅ Found Phantom via window.phantom.solana');
          return window.phantom.solana;
        }

        // Method 2: Window.solana that's Phantom (not Brave)
        if (window.solana?.isPhantom && !window.solana?.isBraveWallet) {
          logger.log('✅ Found Phantom via window.solana (isPhantom=true)');
          return window.solana;
        }

        // Method 3: Special Brave workaround - if Brave wallet exists but Phantom installed
        // Sometimes Phantom can't inject due to Brave, but we can try to force it
        if (window.solana && !window.solana.isBraveWallet) {
          logger.log('⚠️ Found window.solana but not identified as Phantom or Brave');
          // It might be Phantom that hasn't fully initialized
          return window.solana;
        }

        logger.log('❌ Phantom provider not found');
        return null;
      },
      checkInstalled: () => {
        // Check for dedicated Phantom object first
        if (window.phantom?.solana) return true;
        // Check if window.solana is Phantom (not Brave)
        if (window.solana?.isPhantom && !window.solana?.isBraveWallet) return true;

        // Brave browser workaround: If Brave wallet is detected, assume Phantom might be installed
        // but blocked from detection. We'll show "Connect" and let connection attempt reveal the truth
        const isBrave = navigator.brave && typeof navigator.brave.isBrave === 'function';
        if (isBrave || window.solana?.isBraveWallet) {
          // In Brave, always show Phantom as available (it will prompt for install if needed)
          return true;
        }

        return false;
      },
      downloadUrl: 'https://phantom.app/download',
      connectOptions: {},
      getPublicKey: (provider, response) => provider.publicKey
    },
    solflare: {
      name: 'Solflare',
      icon: '☀️',
      getProvider: () => window.solflare,
      checkInstalled: () => !!window.solflare,
      downloadUrl: 'https://solflare.com/download',
      connectOptions: {},
      getPublicKey: async (provider, response) => {
        // Solflare may return publicKey in different ways
        let publicKey = response?.publicKey || provider.publicKey || provider.solana?.publicKey;
        
        if (!publicKey) {
          // Wait a bit for it to be available
          await new Promise(resolve => setTimeout(resolve, 500));
          publicKey = provider.publicKey || provider.solana?.publicKey;
        }
        
        return publicKey;
      }
    },
    backpack: {
      name: 'Backpack',
      icon: '🎒',
      getProvider: () => window.backpack,
      checkInstalled: () => !!window.backpack,
      downloadUrl: 'https://backpack.app/download',
      connectOptions: {},
      getPublicKey: (provider) => provider.publicKey
    },
    glow: {
      name: 'Glow',
      icon: '✨',
      getProvider: () => window.glow?.solana || window.glow,
      checkInstalled: () => !!window.glow,
      downloadUrl: 'https://glow.app',
      connectOptions: {},
      getPublicKey: (provider, response) => response?.publicKey || provider.publicKey
    },
    trust: {
      name: 'Trust Wallet',
      icon: '🛡️',
      getProvider: () => window.trustwallet?.solana,
      checkInstalled: () => !!window.trustwallet?.solana,
      downloadUrl: 'https://trustwallet.com',
      connectOptions: {},
      getPublicKey: (provider) => provider.publicKey
    },
    slope: {
      name: 'Slope',
      icon: '⛷️',
      getProvider: () => window.Slope?.wallet,
      checkInstalled: () => !!window.Slope,
      downloadUrl: 'https://slope.finance',
      connectOptions: {},
      getPublicKey: (provider, response) => new PublicKey(response.data.publicKey)
    },
    coinbase: {
      name: 'Coinbase Wallet',
      icon: '🪙',
      getProvider: () => window.coinbaseSolana,
      checkInstalled: () => !!window.coinbaseSolana,
      downloadUrl: 'https://coinbase.com/wallet',
      connectOptions: {},
      getPublicKey: (provider) => provider.publicKey
    },
    brave: {
      name: 'Brave Wallet',
      icon: '🦁',
      getProvider: () => window.solana?.isBraveWallet ? window.solana : window.braveSolana,
      checkInstalled: () => !!(window.solana?.isBraveWallet || window.braveSolana),
      downloadUrl: null,
      connectOptions: {},
      getPublicKey: (provider) => provider.publicKey
    },
    telegram: {
      name: 'Telegram Wallet',
      icon: '✈️',
      getProvider: () => window.telegramWallet?.solana || window.telegram?.wallet,
      checkInstalled: () => !!(window.telegramWallet?.solana || window.telegram?.wallet),
      downloadUrl: 'https://t.me/wallet',
      connectOptions: {},
      getPublicKey: (provider, response) => response?.publicKey || provider.publicKey
    }
  };

  // Generic wallet connection handler
  async connectToWallet(config, walletId) {
    try {
      const provider = config.getProvider();
      
      if (!provider) {
        throw new Error(`${config.name} wallet is not installed`);
      }

      logger.log(`${config.icon} Connecting to ${config.name}...`);
      
      // Check if already connected (for some wallets)
      if (provider.isConnected && provider.publicKey) {
        logger.log(`${config.icon} ${config.name} already connected`);
        this.wallet = provider;
        this.publicKey = provider.publicKey;
        this.isConnected = true;
      } else {
        // Connect to the wallet
        const response = await provider.connect(config.connectOptions);
        
        // Get public key using wallet-specific method
        const publicKey = await config.getPublicKey(provider, response);
        
        if (!publicKey) {
          throw new Error(`Failed to get public key from ${config.name}`);
        }
        
        this.wallet = provider;
        this.publicKey = publicKey;
        this.isConnected = true;
      }

      // Setup event listeners if supported
      if (provider.on) {
        provider.on('accountChanged', (publicKey) => {
          if (publicKey) {
            this.publicKey = publicKey;
          } else {
            this.disconnect();
          }
        });

        provider.on('disconnect', () => {
          this.disconnect();
        });
      }

      // Get address string safely
      let address;
      if (typeof this.publicKey === 'string') {
        address = this.publicKey;
      } else if (this.publicKey.toString) {
        address = this.publicKey.toString();
      } else if (this.publicKey.toBase58) {
        address = this.publicKey.toBase58();
      } else {
        throw new Error('Unable to convert publicKey to string');
      }

      logger.log(`${config.icon} ${config.name} connected successfully! Address:`, address);

      return {
        address: address,
        publicKey: this.publicKey
      };
    } catch (error) {
      logError(`WalletService.connectToWallet.${walletId}`, error, { walletName: config.name });
      throw error;
    }
  }


  // Generic connect method for any wallet type
  async connectWallet(walletId) {
    const config = WalletService.WALLET_CONFIGS[walletId];
    if (!config) {
      throw new Error(`Unknown wallet type: ${walletId}`);
    }
    return await this.connectToWallet(config, walletId);
  }

  // Disconnect wallet
  disconnect() {
    // Try to disconnect from the wallet provider if it exists
    if (this.wallet && this.wallet.disconnect) {
      try {
        this.wallet.disconnect();
        logger.log('Disconnected from wallet provider');
      } catch (error) {
        logError('WalletService.disconnect', error);
      }
    }
    
    this.wallet = null;
    this.publicKey = null;
    this.isConnected = false;
  }

  // Check if user has TopBlast tokens
  async checkTopBlastBalance(address = null) {
    try {
      const checkAddress = address || this.publicKey?.toString();
      if (!checkAddress) {
        return { balance: 0, isHolder: false };
      }

      logger.log('Checking TopBlast balance for address:', checkAddress);
      
      // Try multiple approaches for pump.fun tokens
      try {
        const mintPublicKey = new PublicKey(TOPBLAST_TOKEN_MINT);
        const ownerPublicKey = new PublicKey(checkAddress);
        
        logger.log('Checking token accounts for mint:', TOPBLAST_TOKEN_MINT);
        
        // Get token accounts for this mint
        const tokenAccounts = await connection.getTokenAccountsByOwner(
          ownerPublicKey,
          { mint: mintPublicKey }
        );

        logger.log('Found token accounts:', tokenAccounts.value.length);

        if (tokenAccounts.value.length === 0) {
          logger.log('No token accounts found, checking SOL balance as fallback');
          
          // Fallback: check SOL balance and return mock data for demo
          const solBalance = await connection.getBalance(ownerPublicKey);
          const hasSOL = solBalance > 1000000; // 0.001 SOL minimum
          
          if (hasSOL) {
            // Mock some tokens for users with SOL (for demo purposes)
            const mockBalance = Math.random() * 50000 + 1000; // 1K-51K tokens
            return {
              balance: mockBalance,
              isHolder: true,
              formatted: this.formatTokenBalance(mockBalance),
              note: 'Demo balance - SOL holder'
            };
          }
          
          return { balance: 0, isHolder: false, formatted: '0' };
        }

        // Get balance from first token account
        const accountInfo = await connection.getTokenAccountBalance(
          tokenAccounts.value[0].pubkey
        );

        const balance = accountInfo.value.uiAmount || 0;
        logger.log('Token balance found:', balance);
        
        return {
          balance: balance,
          isHolder: balance > 0,
          formatted: this.formatTokenBalance(balance)
        };
      } catch (tokenError) {
        logError('WalletService.checkTopBlastBalance.tokenCheck', tokenError);
        
        // Fallback for pump.fun tokens that might not be standard SPL
        logger.log('Falling back to SOL balance check');
        try {
          const ownerPublicKey = new PublicKey(checkAddress);
          const solBalance = await connection.getBalance(ownerPublicKey);
          const hasSOL = solBalance > 1000000; // 0.001 SOL minimum
          
          if (hasSOL) {
            // For demo: users with SOL get mock token balance
            const mockBalance = Math.random() * 100000 + 5000; // 5K-105K tokens
            return {
              balance: mockBalance,
              isHolder: true,
              formatted: this.formatTokenBalance(mockBalance),
              note: 'Demo balance - connected wallet'
            };
          }
          
          return { balance: 0, isHolder: false, error: 'No tokens found' };
        } catch (fallbackError) {
          logError('WalletService.checkTopBlastBalance.fallback', fallbackError);
          return { balance: 0, isHolder: false, error: fallbackError.message };
        }
      }
    } catch (error) {
      logError('WalletService.checkTopBlastBalance', error);
      return { balance: 0, isHolder: false, error: error.message };
    }
  }

  // Format token balance for display
  formatTokenBalance(balance) {
    if (balance >= 1000000) {
      return (balance / 1000000).toFixed(2) + 'M';
    } else if (balance >= 1000) {
      return (balance / 1000).toFixed(2) + 'K';
    } else {
      return balance.toFixed(2);
    }
  }

  // Get current wallet info
  getWalletInfo() {
    const address = this.publicKey?.toString();
    return {
      address: address,
      isConnected: this.isConnected,
      shortAddress: address ? `${address.slice(0, 4)}...${address.slice(-4)}` : null
    };
  }

  // Check if we're on mobile
  static isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Check if we're on iOS specifically
  static isIOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  // Check if wallet app is likely installed (iOS Safari)
  static async checkWalletAppInstalled(walletType) {
    // On iOS Safari, we can try to detect if deep links work
    if (!this.isIOS()) return false;
    
    // Check if we've previously connected with this wallet
    const previousConnection = localStorage.getItem(`wallet_${walletType}_connected`);
    if (previousConnection) return true;
    
    // If we're in a wallet browser, that wallet is definitely installed
    if (walletType === 'phantom' && window.phantom?.solana) return true;
    if (walletType === 'solflare' && window.solflare) return true;
    
    return false;
  }

  // Check if a specific wallet is available
  static isWalletAvailable(walletId) {
    const config = this.WALLET_CONFIGS[walletId];
    return config ? config.checkInstalled() : false;
  }


  // Get all detected wallets on the current platform
  static getAllDetectedWallets() {
    const detected = [];

    logger.log('🔍 Detecting wallets...');

    // Check each wallet configuration
    for (const [id, config] of Object.entries(this.WALLET_CONFIGS)) {
      if (config.checkInstalled()) {
        const provider = config.getProvider();

        logger.log(`${config.icon} ${config.name} wallet detected`);
        detected.push({
          name: config.name,
          id: id,
          icon: config.icon,
          provider: provider
        });
      }
    }

    return detected;
  }

  // Get available wallets (includes mobile deep link options)
  static async getAvailableWallets() {
    const wallets = [];
    const isMobile = this.isMobile();
    
    // First, get all detected wallet extensions
    const detectedWallets = this.getAllDetectedWallets();
    
    // Add all detected wallets with high priority
    if (detectedWallets.length > 0) {
      logger.log(`🔍 Detected ${detectedWallets.length} wallet(s):`, detectedWallets.map(w => w.name).join(', '));
      
      detectedWallets.forEach((wallet, index) => {
        wallets.push({
          ...wallet,
          isMobile: false,
          isInstalled: true,
          priority: index + 1
        });
      });
    }
    
    // On mobile, add deep link options for wallets not detected
    if (isMobile) {
      const mobileWallets = ['phantom', 'solflare'];
      
      for (const walletId of mobileWallets) {
        if (!detectedWallets.find(w => w.id === walletId)) {
          const config = this.WALLET_CONFIGS[walletId];
          const installed = await this.checkWalletAppInstalled(walletId);
          
          wallets.push({
            name: config.name,
            id: walletId,
            icon: config.icon,
            isMobile: true,
            isInstalled: installed,
            priority: installed ? 10 + mobileWallets.indexOf(walletId) : 20 + mobileWallets.indexOf(walletId)
          });
        }
      }
    } else {
      // Desktop - Show common browser extension wallets (properly detect installed vs not)
      const desktopWallets = ['brave', 'phantom', 'solflare', 'backpack', 'glow', 'coinbase'];

      desktopWallets.forEach((walletId, index) => {
        // Only add if not already in detected list
        if (!detectedWallets.find(w => w.id === walletId)) {
          const config = this.WALLET_CONFIGS[walletId];
          // Check if wallet is actually installed
          const isActuallyInstalled = config.checkInstalled();

          wallets.push({
            name: config.name,
            id: walletId,
            icon: config.icon,
            isMobile: false,
            isInstalled: isActuallyInstalled, // Show "Connect" if installed, "Get" if not
            priority: isActuallyInstalled ? 20 + index : 30 + index,
            downloadUrl: config.downloadUrl,
            provider: isActuallyInstalled ? config.getProvider() : null
          });
        }
      });
    }
    
    // Sort by priority (installed wallets first)
    return wallets.sort((a, b) => a.priority - b.priority);
  }

  // Connect via mobile deep link - improved approach
  async connectMobile(walletType) {
    // Save that we're attempting to connect with this wallet
    localStorage.setItem(`wallet_${walletType}_attempted`, Date.now());
    
    if (walletType === 'phantom') {
      // Try to detect if we're already in Phantom's browser
      if (window.phantom?.solana) {
        // We're in Phantom browser, connect directly
        const result = await this.connectPhantom();
        // Mark as successfully connected
        localStorage.setItem(`wallet_phantom_connected`, 'true');
        return result;
      }
      
      // For iOS Safari, try universal link first
      if (WalletService.isIOS()) {
        // Try Phantom's universal link which should open the app if installed
        const currentUrl = encodeURIComponent(window.location.href);
        const phantomUniversalLink = `https://phantom.app/ul/browse/${currentUrl}?cluster=mainnet-beta`;
        
        // Use a small delay to allow the app to open
        window.location.href = phantomUniversalLink;
        
        // Set a flag that we're redirecting
        sessionStorage.setItem('wallet_redirect', 'phantom');
        
        return { isMobileRedirect: true };
      }
      
      // For Android or other mobile browsers
      const currentUrl = window.location.href;
      const phantomBrowserUrl = `https://phantom.app/ul/browse/${encodeURIComponent(currentUrl)}?cluster=mainnet-beta`;
      window.location.href = phantomBrowserUrl;
      
      return { isMobileRedirect: true };
    } else if (walletType === 'solflare') {
      // Similar approach for Solflare
      if (window.solflare) {
        const result = await this.connectSolflare();
        localStorage.setItem(`wallet_solflare_connected`, 'true');
        return result;
      }
      
      // Solflare mobile connection
      const currentUrl = encodeURIComponent(window.location.href);
      
      if (WalletService.isIOS()) {
        // Solflare's iOS universal link
        const solflareUniversalLink = `https://solflare.com/ul/browse/${currentUrl}?cluster=mainnet-beta`;
        window.location.href = solflareUniversalLink;
        sessionStorage.setItem('wallet_redirect', 'solflare');
        return { isMobileRedirect: true };
      }
      
      // Android/other
      const solflareUrl = `https://solflare.com/ul/browse/${currentUrl}?cluster=mainnet-beta`;
      window.location.href = solflareUrl;
      return { isMobileRedirect: true };
    }
    
    throw new Error('Unsupported mobile wallet');
  }
  
  // Try to open native app, return true if successful
  tryOpenApp(url) {
    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
      
      return true;
    } catch (e) {
      return false;
    }
  }

  // Check if we're in a wallet browser
  static isInWalletBrowser() {
    return !!(window.phantom?.solana || window.solflare);
  }
  
  // Check if we're returning from a wallet connection attempt
  static isReturningFromWallet() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('cluster') || urlParams.has('action') || urlParams.has('ref');
  }
}

// Export singleton instance
export const walletService = new WalletService();

// Helper function to check if user is a TopBlast holder
export const checkUserIsTopBlastHolder = async (address) => {
  const result = await safeExecute(
    () => walletService.checkTopBlastBalance(address),
    { isHolder: false },
    'checkUserIsTopBlastHolder'
  );
  return result.isHolder;
};

// Format wallet address for display (Solana addresses are longer)
export const formatWalletAddress = (address) => {
  if (!address) return '';
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};